import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding, resolveLogoSrc } from "@/context/BrandingContext";
import { Link } from "react-router-dom";
import { Coins, ImageOff, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// Branding has its own card below, so keep those keys out of the generic list.
const BRAND_KEYS = ["brand_title", "brand_logo_url"];
const WEEKDAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

export default function Settings() {
  const { user } = useAuth();
  const canEdit = user?.role === "ADMIN";
  const canViewAiUsage = ["ADMIN", "MANAGER"].includes(user?.role);
  const { brandTitle, logoUrl, refreshBranding } = useBranding();
  const [brandTitleDraft, setBrandTitleDraft] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);
  const [settings, setSettings] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [draft, setDraft] = useState({});
  const [accuracy, setAccuracy] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setDraft(Object.fromEntries(r.data.map((s) => [s.key, s.value])));
    });
    api.get("/dispatches").then((r) => setDispatches(r.data));
    api.get("/messages/classification-accuracy").then((r) => setAccuracy(r.data));
  }, []);

  useEffect(() => { setBrandTitleDraft(brandTitle); }, [brandTitle]);

  const saveBrandTitle = async () => {
    const value = brandTitleDraft.trim();
    if (!value) { toast.error("Brand title cannot be empty"); return; }
    try {
      await api.put("/settings/brand_title", { value });
      await refreshBranding();
      toast.success("Brand title updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("logo", file);
    setLogoBusy(true);
    try {
      await api.post("/branding/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshBranding();
      toast.success("Logo updated");
    } catch (e) { toast.error(apiError(e)); }
    finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/branding/logo");
      await refreshBranding();
      toast.success("Logo removed — the brand title is shown instead");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLogoBusy(false); }
  };

  const save = async (key) => {
    try {
      await api.put(`/settings/${key}`, { value: draft[key] });
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">Runtime configuration & dispatch history.</p>
        </div>
        {canViewAiUsage && (
          <Button asChild variant="outline" className="rounded-full" data-testid="open-ai-usage">
            <Link to="/settings/ai-usage"><Coins className="h-4 w-4 mr-2" /> AI Usage & Cost</Link>
          </Button>
        )}
      </div>

      <Card><CardContent className="p-5 space-y-4">
        <div>
          <h2 className="font-heading text-lg font-semibold">Branding</h2>
          <p className="text-xs text-muted-foreground">
            Shown in the sidebar, mobile header, login screen and browser tab.
          </p>
        </div>

        <div className="grid sm:grid-cols-[1fr,2fr,auto] gap-2 items-end" data-testid="setting-brand_title">
          <div>
            <Label>Brand title</Label>
            <div className="text-xs text-muted-foreground">Company name used across the app</div>
          </div>
          <Input value={brandTitleDraft} disabled={!canEdit}
            onChange={(e) => setBrandTitleDraft(e.target.value)} data-testid="brand-title-input" />
          {canEdit && (
            <Button size="sm" variant="outline" className="rounded-full"
              onClick={saveBrandTitle} data-testid="save-brand-title">Save</Button>
          )}
        </div>

        <div className="grid sm:grid-cols-[1fr,2fr,auto] gap-2 items-end">
          <div>
            <Label>Brand logo</Label>
            <div className="text-xs text-muted-foreground">PNG, JPG, WEBP or SVG · max 2 MB</div>
          </div>
          <div className="flex items-center gap-3 min-h-[3.5rem]">
            {logoUrl ? (
              <img src={resolveLogoSrc(logoUrl)} alt={brandTitle}
                className="h-12 w-auto max-w-[12rem] object-contain" data-testid="brand-logo-preview" />
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="brand-logo-empty">
                <ImageOff className="h-4 w-4" /> No logo set
              </div>
            )}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="brand-logo-file-input" />
              <Button size="sm" variant="outline" className="rounded-full" disabled={logoBusy}
                onClick={() => fileRef.current?.click()} data-testid="upload-brand-logo">
                <Upload className="h-4 w-4 mr-1.5" />
                {logoBusy ? "Working…" : logoUrl ? "Update logo" : "Upload logo"}
              </Button>
              {logoUrl && (
                <Button size="sm" variant="ghost" className="rounded-full" disabled={logoBusy}
                  onClick={removeLogo} data-testid="remove-brand-logo">Remove</Button>
              )}
            </div>
          )}
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-5 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Application Settings</h2>
        {settings.filter((s) => !BRAND_KEYS.includes(s.key)).map((s) => (
          <div key={s.key} className="grid sm:grid-cols-[1fr,2fr,auto] gap-2 items-end" data-testid={`setting-${s.key}`}>
            <div>
              <Label className="capitalize">{s.key.replace(/_/g, " ")}</Label>
              <div className="text-xs text-muted-foreground">{s.description}</div>
            </div>
            {s.key === "weekly_summary_start_day" ? (
              <Select value={draft[s.key] || "MONDAY"} disabled={!canEdit}
                onValueChange={(v) => setDraft({ ...draft, [s.key]: v })}>
                <SelectTrigger data-testid={`setting-input-${s.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d) => <SelectItem key={d} value={d} className="capitalize">{d.toLowerCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={draft[s.key] ?? ""} disabled={!canEdit}
                onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })} data-testid={`setting-input-${s.key}`} />
            )}
            {canEdit && <Button size="sm" variant="outline" className="rounded-full" onClick={() => save(s.key)} data-testid={`save-setting-${s.key}`}>Save</Button>}
          </div>
        ))}
      </CardContent></Card>

      {accuracy && (
        <Card><CardContent className="p-5 space-y-2">
          <h2 className="font-heading text-lg font-semibold">AI Classification Accuracy</h2>
          <div className="flex items-baseline gap-2" data-testid="classification-accuracy-stat">
            <span className="text-3xl font-bold">{accuracy.accuracy_pct}%</span>
            <span className="text-sm text-muted-foreground">{accuracy.correct_count}/{accuracy.reviewed_count} reviewed classifications correct</span>
          </div>
        </CardContent></Card>
      )}

      <Card><CardContent className="p-5 space-y-3">
        <h2 className="font-heading text-lg font-semibold">Dispatch History</h2>
        <p className="text-xs text-muted-foreground">Every client email sent from Summaries, including failed attempts.</p>
        {dispatches.length === 0 ? (
          <p className="text-sm text-muted-foreground">No dispatches yet.</p>
        ) : dispatches.map((d) => (
          <div key={d.id} className="flex justify-between items-center border-b border-border py-2 text-sm" data-testid={`dispatch-${d.id}`}>
            <div>
              <div className="font-medium">{d.subject}</div>
              <div className="text-xs text-muted-foreground">{d.recipient_email} · {new Date(d.sent_at).toLocaleString("en-IN")}</div>
              {d.error_text && <div className="text-xs text-destructive">{d.error_text}</div>}
            </div>
            <StatusBadge status={d.status === "SENT" ? "COMPLETED" : d.status} />
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}
